import { useState } from 'react';
import { Check, Copy, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getApiErrorCode, getApiErrorMessage } from '@/client/utils/errors';
import { toast } from 'sonner';
import { SalesError, formatGhs } from '@/client/types/sales';
import { promptActAs } from '../sales-store';
import type { FitParts, SalesGoal } from '@/client/types/sales';
import type { FitWeights } from '@/client/types/sales';

export function StatusBadge({ label, className }: { label: string; className: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium', className)}>
      {label}
    </span>
  );
}

export function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-white py-14 text-center text-sm text-gray-400">
      {label}
    </div>
  );
}

/** Index outage. `upstream_forbidden` is ops — never ask the rep to request access. */
export function MarketplaceIndexState({
  error,
  unavailable = 'Search is unavailable.',
}: {
  error: unknown;
  unavailable?: string;
}) {
  const code = getApiErrorCode(error);
  if (code === 'upstream_forbidden') {
    return <EmptyState label={unavailable} />;
  }
  if (code === 'not_ready' || code === 'upstream_error') {
    return (
      <EmptyState label="The marketplace index did not answer. Try again shortly — this usually clears after a restart." />
    );
  }
  return <EmptyState label={getApiErrorMessage(error, 'Could not load the marketplace index.')} />;
}

export function Panel({
  title,
  children,
  className,
  actions,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}) {
  return (
    <section className={cn('rounded-xl border border-gray-200 bg-white p-5', className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function toastSales(e: unknown): void {
  const code = e instanceof SalesError ? e.code : getApiErrorCode(e) ?? (e as { code?: string })?.code;
  if (code === 'no_actor') promptActAs('do that');
  else if (e instanceof SalesError) toast.error(e.message);
  else toast.error(getApiErrorMessage(e, e instanceof Error ? e.message : 'Something went wrong'));
}

export function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

export { formatGhs };

export function FitBars({
  parts,
  weights,
  goalGap,
  score,
}: {
  parts: FitParts;
  weights: FitWeights;
  goalGap: number;
  score: number;
}) {
  const rows: { key: string; label: string; value: number; weight: number }[] = [
    { key: 'area', label: 'Area demand', value: parts.areaDemand, weight: weights.areaDemand },
    { key: 'act', label: 'Activity', value: parts.activityRecency, weight: weights.activityRecency },
    { key: 'spend', label: 'Spend band', value: parts.spendBand, weight: weights.spendBand },
    { key: 'gap', label: 'Goal gap', value: goalGap, weight: weights.goalGap },
  ];
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-gray-500">Fit score</span>
        <span className="text-sm font-semibold tabular-nums text-gray-900">{Math.round(score * 100)}</span>
      </div>
      {rows.map((r) => (
        <div key={r.key} className="grid grid-cols-[7rem_1fr_2.5rem] items-center gap-2 text-[11px] text-gray-500">
          <span className="truncate">
            {r.label}
            <span className="ml-1 text-gray-400">×{Math.round(r.weight * 100)}</span>
          </span>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.round(r.value * 100)}%` }} />
          </div>
          <span className="tabular-nums text-right">{Math.round(r.value * 100)}</span>
        </div>
      ))}
    </div>
  );
}

export function goalGapFor(achieved: SalesGoal[], goal: SalesGoal): number {
  return achieved.includes(goal) ? 0 : 1;
}

export const selectCls =
  'rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800';
export const btnPrimary =
  'inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white transition-transform hover:bg-slate-800 active:scale-[0.97] disabled:opacity-40';
export const btnGhost =
  'inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 active:scale-[0.97]';

export function compactGhs(n: number): string {
  if (n >= 1_000_000_000) return `GHS ${(n / 1_000_000_000).toFixed(2)}bn`;
  if (n >= 1_000_000) return `GHS ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `GHS ${Math.round(n / 1_000)}k`;
  return formatGhs(n);
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function timeLeftLabel(expiresAt: string, now: number): string {
  const ms = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return 'Overdue';
  const h = Math.round(ms / 3_600_000);
  if (h < 48) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}

export function QuotaBar({ won, quota, className }: { won: number; quota: number; className?: string }) {
  const ratio = quota > 0 ? won / quota : 0;
  return (
    <div className={className}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-gray-900">
          {formatGhs(won)} <span className="font-normal text-gray-400">of {formatGhs(quota)} quota</span>
        </span>
        <span className="text-sm tabular-nums text-gray-500">{pct(ratio)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-emerald-600 transition-[width] duration-300 ease-out"
          style={{ width: `${Math.min(100, ratio * 100)}%` }}
        />
      </div>
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === o.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// --- Transferable prospect info -------------------------------------------
// One-click actions so reps act on a prospect (call, WhatsApp, email, share a
// link) instead of hand-selecting and copy-pasting. Used wherever a phone,
// email or shareable URL is shown.

function copyToClipboard(value: string, onDone: () => void): void {
  if (!value) return;
  navigator.clipboard?.writeText(value).then(onDone, () => {});
}

/** Ghana-normalised number for a wa.me link: 0XXXXXXXXX -> 233XXXXXXXXX. */
function waNumber(phone: string): string | null {
  const d = phone.replace(/\D/g, '');
  if (!d) return null;
  if (d.startsWith('233')) return d;
  if (d.startsWith('0')) return `233${d.slice(1)}`;
  if (d.length === 9) return `233${d}`; // 24XXXXXXX with no leading 0
  return d;
}

export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: string | null | undefined;
  label?: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={() => copyToClipboard(value, () => {
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      })}
      className={cn('inline-flex shrink-0 items-center text-gray-400 hover:text-gray-700', className)}
      title={label}
      aria-label={label}
    >
      {done ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function PhoneValue({ phone, className }: { phone?: string | null; className?: string }) {
  if (!phone) return <span className="text-gray-400">—</span>;
  const wa = waNumber(phone);
  return (
    <span className={cn('inline-flex items-center gap-1.5 tabular-nums', className)}>
      {wa ? (
        <a
          href={`https://wa.me/${wa}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-900 hover:text-green-700"
          title="Open in WhatsApp"
        >
          {phone}
        </a>
      ) : (
        <a href={`tel:${phone}`} className="text-gray-900 hover:text-slate-700">
          {phone}
        </a>
      )}
      <CopyButton value={phone} label="Copy number" />
    </span>
  );
}

export function EmailValue({ email, className }: { email?: string | null; className?: string }) {
  if (!email) return <span className="text-gray-400">—</span>;
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <a href={`mailto:${email}`} className="break-all text-gray-900 hover:text-slate-700">
        {email}
      </a>
      <CopyButton value={email} label="Copy email" />
    </span>
  );
}

/** Copies a shareable URL (defaults to the current page) — pairs with the
 *  URL-backed filters so a filtered view or a prospect can be shared as a link. */
export function CopyLinkButton({
  href,
  label = 'Copy link',
  className,
}: {
  href?: string;
  label?: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() =>
        copyToClipboard(href ?? (typeof window !== 'undefined' ? window.location.href : ''), () => {
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        })
      }
      className={cn('inline-flex items-center gap-1.5 text-sm font-medium text-gray-800 hover:underline', className)}
      title={label}
      aria-label={label}
    >
      {done ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
      {done ? 'Copied' : label}
    </button>
  );
}
